import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Paperclip, Trash2, Upload } from 'lucide-react';
import type { Attachment, AttachmentList } from '@mnc/contracts';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { describeError } from '../../lib/errors';
import { pick } from '../../i18n';
import { ConfirmDialog } from '../ui';

const MAX_MB = 25;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Files attached to a record (shared `attachments` table); delivery is per record through /api/files. */
export function AttachmentsPanel({ ownerType, ownerId, canEdit, compact }: { ownerType: string; ownerId: string; canEdit: boolean; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const { me, can } = useAuth();
  const qc = useQueryClient();
  const key = ['attachments', ownerType, ownerId];
  const list = useQuery({ queryKey: key, queryFn: () => api.get<AttachmentList>(`/api/attachments?owner_type=${ownerType}&owner_id=${ownerId}`) });
  const fileInput = useRef<HTMLInputElement>(null);
  const [toDelete, setToDelete] = useState<Attachment | null>(null);
  const invalidate = () => qc.invalidateQueries({ queryKey: key });
  const upload = useMutation({
    mutationFn: (file: File) => api.upload<Attachment>(`/api/attachments?owner_type=${ownerType}&owner_id=${ownerId}`, file),
    onSuccess: () => { toast.success(t('attachments.uploaded')); void invalidate(); },
    onError: (e) => toast.error(describeError(e, t)),
  });
  const remove = useMutation({ mutationFn: (id: string) => api.delete(`/api/attachments/${id}`), onSuccess: () => { toast.success(t('attachments.deleted')); void invalidate(); }, onError: (e) => toast.error(describeError(e, t)) });

  const onPick = (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) { toast.error(t('attachments.tooLarge', { mb: MAX_MB })); return; }
    upload.mutate(file);
    if (fileInput.current) fileInput.current.value = '';
  };

  return (
    <div className={compact ? '' : 'card p-4'}>
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-semibold"><Paperclip size={16} className="text-neutral-500" />{t('attachments.title')}{list.data && <span className="text-xs font-normal text-neutral-500">({list.data.items.length})</span>}</div>
        {canEdit && (
          <>
            <input ref={fileInput} type="file" className="hidden" onChange={(e) => onPick(e.target.files)} />
            <button type="button" className="btn-secondary px-2 py-1" disabled={upload.isPending} onClick={() => fileInput.current?.click()}><Upload size={14} /> {upload.isPending ? t('attachments.uploading') : t('attachments.upload')}</button>
          </>
        )}
      </div>
      {list.data && list.data.items.length === 0 && <p className="text-sm text-neutral-500">{t('attachments.empty')}</p>}
      <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {(list.data?.items ?? []).map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3 py-2 text-sm">
            <a href={a.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1">
              <div className="truncate font-medium text-brand-700 dark:text-brand-100" dir="ltr">{a.file_name}</div>
              <div className="text-xs text-neutral-500">
                {formatSize(a.size)} · {pick(i18n.language, a.uploaded_by_name_en, a.uploaded_by_name_ar)} · {new Date(a.created_at).toLocaleDateString(i18n.language)}
                {(a.description_en || a.description_ar) && <span dir="auto"> · {pick(i18n.language, a.description_en, a.description_ar)}</span>}
              </div>
            </a>
            {(a.uploaded_by === me?.id || canEdit && can('users', 'can_delete')) && <button type="button" className="btn-secondary px-2 py-1" onClick={() => setToDelete(a)} aria-label={t('app.delete')}><Trash2 size={14} /></button>}
          </li>
        ))}
      </ul>
      <ConfirmDialog open={toDelete !== null} title={t('app.delete')} body={toDelete?.file_name} danger onCancel={() => setToDelete(null)} onConfirm={() => { if (toDelete) remove.mutate(toDelete.id); setToDelete(null); }} />
    </div>
  );
}
